// apps/client/src/ui/screens/sell.tsx (M1-T14) — selling from your pockets (and hand): at the shop counter (the shop screen's Sell tab
// embeds the grid) or at the 24-hour drop box outside (this screen, params { where }). Pick any number of items; each picked slot shows
// what it pays, the running total sits beside the grid, and Sell sends them all in one action. Items the shop won't take are dimmed.
import { getEntry, tuning } from '@whiskerton/content';
import type { ItemView, TownInfo } from '@whiskerton/shared';
import { dropBoxPrice, sellPrice } from '@whiskerton/shared/rules/economy';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { getGame, useCommands } from '../../game/instance';
import { sellItems } from '../../state/slices/shop';
import { useGame } from '../../state/store';
import { itemName, useT } from '../i18n';
import { useIcon } from '../items/useIcon';
import { Button, Panel } from '../kit';
import type { ScreenDef } from '../router';

declare module '../router' { interface ScreenParams { sell: { where: 'shop' | 'dropbox' } } }

/** The shop screens' styles (shop, sell, loan, roof), injected once like the creator's (DESIGN.md: cream wells, pills, tabular numbers). */
const CSS = `
.shop-panel { display: grid; gap: 14px; width: min(74cqw, 820px); max-width: 100%; }
.shop-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; flex-wrap: wrap; }
.shop-head .bubble { max-width: 46cqw; }
.shop-list { display: grid; gap: 8px; }
.shop-list h4, .shop-sub { margin: 4px 0 0; font-weight: 900; font-size: 15px; color: var(--wood-700); }
.shop-row { display: grid; grid-template-columns: 48px 1fr auto auto; align-items: center; gap: 12px; background: var(--cream-100);
  border-radius: 16px; padding: 6px 8px 6px 6px; box-shadow: var(--sh-well); }
.shop-row img { width: 48px; height: 48px; object-fit: contain; }
.shop-row .btn { padding: 10px 18px 11px; font-size: 15px; }
.shop-name { font-weight: 900; }
.shop-price { font-weight: 800; color: var(--wood-700); font-variant-numeric: tabular-nums; }
.shop-note { margin: 0; font-weight: 800; color: var(--wood-700); }
.sell-layout { display: grid; grid-template-columns: 1.55fr 1fr; gap: 2.4cqw; align-items: start; }
.sell-side { display: grid; gap: 12px; align-content: start; }
.sell-total { margin: 0; font-weight: 900; font-size: 18px; font-variant-numeric: tabular-nums; }
.sell-cell { position: relative; }
.sell-cell .slot { width: 100%; }
.sell-cell .slot:disabled { opacity: .38; cursor: default; }
.sell-price { position: absolute; left: 50%; bottom: -6px; transform: translateX(-50%); background: var(--leaf-700); color: #fff;
  border-radius: var(--r-pill); font-weight: 900; font-size: 12px; padding: 1px 7px; font-variant-numeric: tabular-nums; pointer-events: none; white-space: nowrap; }
.loan-pay { display: grid; gap: 10px; }
.loan-layout { display: grid; grid-template-columns: 1fr 1fr; gap: 2.4cqw; align-items: start; }
.loan-talk, .loan-side { display: grid; gap: 8px; align-content: start; min-width: 0; }
.loan-talk .bubble { max-width: none; }
.loan-amount { display: grid; grid-template-columns: 1fr auto; gap: 8px; align-items: end; }
.loan-amount .field input { width: 100%; box-sizing: border-box; min-width: 0; }
.loan-quick { display: flex; gap: 8px; flex-wrap: wrap; }
.loan-quick .btn { padding: 10px 18px 11px; font-size: 15px; }
.swatch-row { border: 0; padding: 0; margin: 0; display: flex; flex-wrap: wrap; gap: 10px; }
.swatch { min-width: 52px; height: 52px; border: 0; border-radius: var(--r-swatch); background: var(--swatch, var(--cream-100)); font: inherit;
  box-shadow: var(--sh-soft); cursor: pointer; }
.swatch[aria-pressed='true'] { box-shadow: inset 0 0 0 4px var(--leaf-700), var(--sh-soft); }
.roof-layout { display: grid; grid-template-columns: auto minmax(220px, 1fr); gap: 2.4cqw; align-items: center; }
.roof-layout.no-preview { grid-template-columns: minmax(220px, 1fr); }
.roof-side { display: grid; gap: 12px; align-content: start; justify-items: start; }
.roof-preview { display: block; width: min(36cqw, 260px, 46cqh); aspect-ratio: 1; object-fit: contain; }
`;
let styled = false;
export function injectShopStyle(): void {
  if (styled || typeof document === 'undefined') return;
  styled = true;
  const el = document.createElement('style');
  el.dataset.owner = 'shop';
  el.textContent = CSS;
  document.head.appendChild(el);
}

type Where = 'shop' | 'dropbox';
export const priceAt = (where: Where, item: ItemView, town: Pick<TownInfo, 'nativeFruit'>): number =>
  (where === 'shop' ? sellPrice(item.catalogId, town) : dropBoxPrice(item.catalogId, town)) * item.qty;

function Cell({ item, index, picked, price, onPick }: { item: ItemView | undefined; index: number; picked: boolean; price: number; onPick(): void }) {
  const t = useT();
  const icon = useIcon(item?.catalogId ?? null);
  const e = item && getEntry(item.catalogId);
  const name = e && e.kind !== 'world' ? itemName(e.name) : t('shop.emptySlot', { n: index + 1 });
  return (
    <div className="sell-cell">
      <button type="button" className={`slot${picked ? ' slot-selected' : ''}`} aria-label={name} aria-pressed={picked} disabled={!item || price <= 0} onClick={onPick}>
        {item && <img src={icon} alt="" />}
        {item && item.qty > 1 && <span className="slot-qty">{item.qty}</span>}
      </button>
      {picked && <span className="sell-price">{t('shop.slotPrice', { price })}</span>}
    </div>
  );
}

/** The pockets (and the hand slot while you hold something) with multi-select, the total and the Sell button. */
export function SellGrid({ where, open = true, onSold }: { where: Where; open?: boolean; onSold?: (earned: number) => void }) {
  injectShopStyle();
  const t = useT();
  const cmd = useCommands();
  const { you, town, picks, toggle, clear } = useGame(useShallow((s) => ({ you: s.you, town: s.town, picks: s.shopSellPicks, toggle: s.shopToggleSell, clear: s.shopClearSell })));
  const [busy, setBusy] = useState(false);
  useEffect(() => clear, [clear]);                                       // picks never outlive the grid
  if (!you || !town) return null;
  const hand = you.items.find((i) => i.at.loc === 'hand');
  const pockets = you.items.filter((i) => i.at.loc === 'pockets');
  const bySlot = new Map(pockets.map((i) => [i.at.loc === 'pockets' ? i.at.slot : -1, i]));
  const sellable = [...(hand ? [hand] : []), ...pockets].filter((i) => priceAt(where, i, town) > 0);
  const chosen = sellable.filter((i) => picks.includes(i.id));           // picks of items that left meanwhile drop out
  const total = chosen.reduce((a, i) => a + priceAt(where, i, town), 0);
  const sell = () => {
    const ids = chosen.map((i) => i.id);
    setBusy(true);
    clear();
    void sellItems(cmd.act, ids, where).then((r) => {
      setBusy(false);
      if (!r.ok) return;
      getGame().sound?.sfx('coin');
      onSold?.(r.r.earned);
    });
  };
  const cell = (item: ItemView | undefined, n: number) => (
    <Cell key={item?.id ?? `empty-${n}`} item={item} index={n} picked={!!item && picks.includes(item.id)} price={item ? priceAt(where, item, town) : 0}
      onPick={() => { if (item) toggle(item.id); }} />
  );
  return (
    <div className="sell-layout">
      <div style={{ display: 'grid', gap: '1.4cqw', alignContent: 'start' }}>
        {hand && <div style={{ width: 'calc((100% - 4.4cqw) / 5)' }}>{cell(hand, -1)}</div>}
        <div className="slot-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1.1cqw' }}>
          {Array.from({ length: tuning.items.pocketSlots }, (_, n) => cell(bySlot.get(n), n))}
        </div>
      </div>
      <div className="sell-side">
        <p className="shop-note">{t(sellable.length ? 'shop.sellPick' : 'shop.nothingToSell')}</p>
        <p className="sell-total">{t('shop.sellTotal', { total })}</p>
        <Button disabled={!open || busy || chosen.length === 0} onClick={sell}>{t('shop.sellButton')}</Button>
      </div>
    </div>
  );
}

function Sell({ where, close }: { where: Where; close: () => void }) {
  injectShopStyle();
  const t = useT();
  const { space, openScreen } = useGame(useShallow((s) => ({ space: s.space, openScreen: s.openScreen })));
  const here = where === 'dropbox' ? space === 'out' : space === 'shop';
  useEffect(() => { if (!here) close(); }, [here, close]);               // walked off or the space changed
  return (
    <Panel title={t(where === 'dropbox' ? 'shop.dropBoxTitle' : 'shop.sell')} className="shop-panel">
      {where === 'dropbox' && (
        <div className="shop-head">
          <p className="shop-note">{t('shop.dropBoxNote', { pct: Math.round(tuning.shop.dropBoxRate * 100) })}</p>
          <Button variant="quiet" onClick={() => openScreen('loan', { at: 'machine' })}>{t('shop.payAtMachine')}</Button>
        </div>
      )}
      <SellGrid where={where} />
    </Panel>
  );
}
export const screen: ScreenDef<'sell'> = { id: 'sell', layer: 'panel', component: ({ params, close }) => <Sell where={params.where} close={close} /> };
